import React, { useEffect, useMemo } from 'react';
import { connect } from 'react-redux';
import moment from 'moment';
import styled from 'styled-components';
import lodash from 'lodash';
import { Grid, Col } from '../../../ui-kit';
// Redux imports
import { selectors as groupSelectors } from '../../../../state/modules/groups';
import { selectors as combinedScheduleSelectors } from '../../../../state/modules/combined-schedules';
import { selectors as membershipSelectors } from '../../../../state/modules/memberships';
import { selectors as dashboardSelectors } from '../../../../state/modules/dashboards';
// Other utilities
import RidershipFilterObject from './RidershipFilterObject';
import DashboardRegistrationManager from '../../DashboardRegistrationManager';
import Settings from '../shared/Filter/FilterSettings';
import { FilterRow } from '../shared/Filter/Styles';
import { formatWeekday, formatTimeOnlyShort } from '../../../../utils/time';
import Loader from '../../../general/Loader';
import colors, { fontColors } from '../../../../utils/colors';

const StyledCol = styled(Col)`
  text-align: center;
  padding: 0;
`;

const StyledCell = styled(Col)`
  text-align: center;
  padding: 0;
  background: ${props => colors[props.combinedstate] || 'rgba(0,0,0,0)'};
  color: ${props => fontColors[props.combinedstate] || 'white'};
`;

const Cell = ({ filter, allMembers, time }) => {
  const crewSize = filter.filter(allMembers, time).length;

  return (
    <StyledCell xs={1} md={1} lg={1} combinedstate={minimumCrewColor(filter.minimumCrew, crewSize)}>
      {crewSize}
    </StyledCell>
  );
};

const RidershipDetailsHeader = ({ timeSlots, dashboardDatetime }) => {
  const countByDay = useMemo(() => {
    // need to convert to iso string. Otherwise, the date is converted to a string
    // using toString. This is because the date is the key in the object, and keys
    // must be strings
    const grouped = lodash.groupBy(timeSlots, d => d.clone().startOf('day').toISOString());
    return Object.entries(grouped).map(([date, slots]) => [date, slots.length]);
  }, [timeSlots]);

  return (
    <>
      <FilterRow header="true">
        <Col xs={2} md={2} lg={2}>
          <strong>{/* leave empty col for styling purposes */}</strong>
        </Col>
        {countByDay.map(([date, count]) => (
          <StyledCol xs={count} md={count} lg={count} key={date}>
            <strong>{formatWeekday(date)}</strong>
          </StyledCol>
        ))}
      </FilterRow>
      <FilterRow subheader="true">
        <Col xs={2} md={2} lg={2}>
          <strong>{/* leave empty col for styling purposes*/}</strong>
        </Col>
        {timeSlots.map(time => (
          <StyledCol xs={1} md={1} lg={1} key={time}>
            <span>
              {!dashboardDatetime.locked && moment(time).isBefore()
                ? I18n.t('now').toUpperCase()
                : formatTimeOnlyShort(time)}
            </span>
          </StyledCol>
        ))}
      </FilterRow>
    </>
  );
};

const minimumCrewColor = (minimumCrew, allMembers) => {
  if (!minimumCrew) return 'not_set';

  if (allMembers < minimumCrew) return 'below_required';
  else if (allMembers === minimumCrew) return 'on_required';
  else return 'above_buffer';
};

const RidershipDetailsWidget = props => {
  useEffect(() => {
    props.dashboardAPIManager.fetchGroups();
    props.dashboardAPIManager.fetchSkills();
    props.dashboardAPIManager.fetchMemberships();
    props.dashboardAPIManager.fetchStandbyDutyFunctions();
    props.dashboardAPIManager.fetchAvailabilityCodes({ params: { in_hierarchy: true } });
  }, []);

  useEffect(() => {
    const groupIds = Object.keys(props.groupsById);
    if (groupIds.length === 0) return;

    new DashboardRegistrationManager().registerForCombinedScheduleUpdates({
      groupIds,
      type: 'membership',
      look_ahead: 432_000
    }); // 5 days expressed in seconds
  }, [JSON.stringify(Object.keys(props.groupsById).sort()), props.dashboardDatetime.dateTime]);

  const timeSlots = useMemo(() => {
    const times = [];
    const now = moment(props.dashboardDatetime.dateTime).clone();
    // the following variables define the starting hours for shifts,
    // for widgets that have not been setup with the new settings, default to 10 / 19
    const dayCheckpoint = Number.isInteger(props.dayCheckpoint)
      ? { hour: Number(props.dayCheckpoint || 10), minute: 0 }
      : props.dayCheckpoint;
    const nightCheckpoint = Number.isInteger(props.nightCheckpoint)
      ? { hour: Number(props.nightCheckpoint || 19), minute: 0 }
      : props.nightCheckpoint;
    for (let i = 0; i < 5; i++) {
      const start = now.clone().startOf('day').add(i, 'days');
      const day = start.clone().add(dayCheckpoint.hour, 'hour').add(dayCheckpoint.minute, 'minutes');
      const night = start.clone().add(nightCheckpoint.hour, 'hour').add(nightCheckpoint.minute, 'minutes');

      times.push(day, night);
    }

    // filter out all the timeslots that are in the past
    const afterNow = times.filter(t => t.isAfter(now));

    // add NOW in front of the list, and keep the first 10
    return [now].concat(afterNow).slice(0, 10);
  }, [props.dashboardDatetime, props.dayCheckpoint, props.nightCheckpoint]);

  const filters = useMemo(() => props.filters.map(f => new RidershipFilterObject(f)), [props.filters]);

  // must show ridership details for today + 4 days, divided in day (10:00) and night shifts (19:00)
  if (filters.length === 0) return <span>{I18n.t('no_results_found')}</span>;
  if (!props.initialLoad || props.combinedScheduleLoading) return <Loader />;
  return (
    <Grid fluid>
      <RidershipDetailsHeader timeSlots={timeSlots} dashboardDatetime={props.dashboardDatetime} />
      {filters.map((filter, index) => (
        <FilterRow key={index}>
          <Col xs={2} md={2} lg={2}>
            <strong>{filter.title}</strong>
          </Col>
          {timeSlots.map(time => (
            <Cell key={time} filter={filter} time={time} allMembers={props.allMembers} />
          ))}
        </FilterRow>
      ))}
    </Grid>
  );
};

Object.assign(RidershipDetailsWidget, {
  SettingsContent: Settings,
  // A filter consists of: title, group_ids, skill_ids, duty_function_ids, availability_code_ids
  defaultSettings: {
    filters: [],
    dayCheckpoint: { hour: 10, minute: 0 },
    nightCheckpoint: { hour: 19, minute: 0 }
  }
});

const mapStateToProps = state => ({
  groupsById: groupSelectors.byId(state),
  allMembers: combinedScheduleSelectors.allMembershipStatus(state),
  initialLoad: groupSelectors.initialLoadComplete(state) && membershipSelectors.initialLoadComplete(state),
  combinedScheduleLoading: combinedScheduleSelectors.loading(state),
  dashboardDatetime: dashboardSelectors.selectDashboardDatetime(state)
});

export default connect(mapStateToProps)(RidershipDetailsWidget);
